import { SettlementTriggerPaymentMethods } from '@ankor-io/common/domain/pay/settlements'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import {
  AWXActivityEventType,
  AWXCardActivityEventStatus,
  AWXConnectedAccountTransferActivityEventStatus,
  AWXTransferActivityEventStatus,
  PaymentMethod,
  type TransactionPayload,
  TransactionSettlementStatus,
  TransactionStatus,
  TransactionType,
} from '@ankor-io/common/types/transaction/transaction'

import {
  getCardTransactionStatusColour,
  getConnectedAccountTransferStatusColour,
  getConnectedAccountTransferStatusText,
  getTransferStatusColour,
  getTransferStatusText,
} from './transaction'
import { getCardPaymentStatusText } from './transaction.payment.status'

type ExpensePaymentStatusTransaction = Pick<
  TransactionPayload,
  'type' | 'paymentMethod' | 'status' | 'settlementStatus' | 'awxActivityEvent'
>

const isCardActivityEventStatus = (status: string): status is AWXCardActivityEventStatus =>
  (Object.values(AWXCardActivityEventStatus) as string[]).includes(status)

const isTransferActivityEventStatus = (status: string): status is AWXTransferActivityEventStatus =>
  (Object.values(AWXTransferActivityEventStatus) as string[]).includes(status)

const isConnectedAccountTransferActivityEventStatus = (
  status: string,
): status is AWXConnectedAccountTransferActivityEventStatus =>
  (Object.values(AWXConnectedAccountTransferActivityEventStatus) as string[]).includes(status)

const UNPAID = { text: 'Unpaid', colour: 'gray' } as const

/**
 * Expenses that are settled with Operations: manual payment methods paid from Operations resources.
 */
const isSettledWithOperations = (transaction: Pick<TransactionPayload, 'paymentMethod'>): boolean =>
  SettlementTriggerPaymentMethods.includes(transaction.paymentMethod)

/**
 * Resolves the Payment Status of an expense: the status of the payment on its payment rail.
 *
 * - AnkorPay card expenses show the card activity status (Pending, Success, Failed, Refunded)
 * - AnkorPay bank transfer expenses show Unpaid once approved, then the payout status once the payout
 *   exists (Processing, Sent, Paid, ...)
 * - AnkorPay connected account transfers show the transfer status (New, Pending, Settled, ...)
 * - APA manual expenses settled with Operations show that settlement: Unpaid while an approved
 *   expense is still owed to Operations, then Paid or Marked as Paid.
 * - Operations manual expenses keep the dash: they are not settled through AnkorPay, so they would
 *   otherwise stay Unpaid forever.
 * - Gratuity always keeps the dash: it is never paid or marked as paid through settlement.
 * - A CHARGE expense keeps the dash too: it is a platform fee deducted from the account, never
 *   paid through a payment rail.
 * - A denied or unapproved expense has nothing to pay yet, so it keeps the dash.
 *
 * Returns null when there is nothing to show yet, in which case the caller renders a dash.
 *
 * @param accountType the module of the expense, since only APA manual expenses are settled with Operations
 */
export const getExpensePaymentStatus = (
  transaction: ExpensePaymentStatusTransaction,
  accountType: AwxConnectedAccountType,
): { text: string; colour: 'green' | 'red' | 'yellow' | 'gray' } | null => {
  const { paymentMethod, awxActivityEvent } = transaction

  if (transaction.type === TransactionType.GRATUITY || transaction.type === TransactionType.CHARGE) {
    return null
  }

  if (paymentMethod === PaymentMethod.ANKOR_PAY || paymentMethod === PaymentMethod.TRANSFER) {
    if (!awxActivityEvent) {
      // an approved bank transfer is still to be paid to its beneficiary until the payout is created
      return paymentMethod === PaymentMethod.TRANSFER && transaction.status === TransactionStatus.APPROVED
        ? UNPAID
        : null
    }

    if (
      awxActivityEvent.type === AWXActivityEventType.CARD_TRANSACTION &&
      isCardActivityEventStatus(awxActivityEvent.status)
    ) {
      return {
        text: getCardPaymentStatusText(awxActivityEvent.status),
        colour: getCardTransactionStatusColour(awxActivityEvent.status),
      }
    }

    if (
      awxActivityEvent.type === AWXActivityEventType.TRANSFER &&
      isTransferActivityEventStatus(awxActivityEvent.status)
    ) {
      return {
        text: getTransferStatusText(awxActivityEvent.status),
        colour: getTransferStatusColour(awxActivityEvent.status),
      }
    }

    if (
      awxActivityEvent.type === AWXActivityEventType.CONNECTED_ACCOUNT_TRANSFER &&
      isConnectedAccountTransferActivityEventStatus(awxActivityEvent.status)
    ) {
      return {
        text: getConnectedAccountTransferStatusText(awxActivityEvent.status),
        colour: getConnectedAccountTransferStatusColour(awxActivityEvent.status),
      }
    }

    return null
  }

  // Operations manual expenses are never settled through AnkorPay
  if (accountType === AwxConnectedAccountType.APA && isSettledWithOperations(transaction)) {
    switch (transaction.settlementStatus) {
      case TransactionSettlementStatus.SETTLED:
        return { text: 'Paid', colour: 'green' }
      case TransactionSettlementStatus.MARKED_AS_SETTLED:
        return { text: 'Marked as Paid', colour: 'green' }
      case TransactionSettlementStatus.PENDING:
        // an approved manual expense awaiting settlement is owed to Operations. Unapproved rows keep
        // the dash: there is nothing to pay until approval
        if (transaction.status === TransactionStatus.APPROVED) {
          return UNPAID
        }
        return null
      default:
        return null
    }
  }

  return null
}
